import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, RotateCcw, Eye, Printer, Download, X, Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FONT_FAMILIES = ["Helvetica", "Times-Roman", "Courier"];
const ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];
const FONT_KEYS = [
  { key: "company_name",    label: "Company Name" },
  { key: "address",         label: "Office Address" },
  { key: "receipt_heading", label: "Receipt Heading" },
  { key: "customer",        label: "Customer Details" },
  { key: "service",         label: "Service Name / Details" },
  { key: "notes",           label: "Notes" },
  { key: "ad_heading",      label: "Advertisement Headings" },
  { key: "ad_list",         label: "Advertisement List" },
  { key: "phone",           label: "Mobile Number" },
  { key: "email",           label: "Email Address" },
  { key: "footer",          label: "Website / Footer" },
];
const CONTACT_FIELDS = [
  { key: "company_name",   label: "Office / Company Name",  type: "text" },
  { key: "address",        label: "Office Address",         type: "textarea" },
  { key: "working_hours",  label: "Working Hours",          type: "text" },
  { key: "legal_phone",    label: "Legal Services Mobile",  type: "text" },
  { key: "property_phone", label: "Property Services Mobile", type: "text" },
  { key: "whatsapp",       label: "WhatsApp Number",        type: "text" },
  { key: "email",          label: "Email Address",          type: "text" },
  { key: "website",        label: "Website Address",        type: "text" },
];

// ------------------ Live A5 Preview (HTML/CSS mirror of the PDF) ---------------
function LivePreview({ settings }) {
  const fonts = settings?.fonts || {};
  const contact = settings?.contact || {};
  const show = contact?.show || {};
  const ads = (settings?.advertisements || []).filter((a) => a.show !== false)
    .sort((a, b) => (a.column ?? 1) - (b.column ?? 1) || (a.order ?? 0) - (b.order ?? 0));
  const cols = ads.reduce((m, a) => {
    const c = a.column || 1;
    (m[c] ||= []).push(a);
    return m;
  }, {});
  const colKeys = Object.keys(cols).map(Number).sort((a, b) => a - b);
  const fontStyle = (key) => {
    const c = fonts[key] || {};
    return {
      fontFamily: c.family === "Times-Roman" ? "Times New Roman, serif" : c.family === "Courier" ? "Courier New, monospace" : "Arial, sans-serif",
      fontSize: `${c.size || 9}pt`,
      fontWeight: c.bold ? 700 : 400,
      color: c.color || "#000",
      textAlign: c.align || "left",
      lineHeight: `${(c.leading || (c.size || 9) * 1.2) / (c.size || 9)}`,
    };
  };
  const bannerColor = settings?.layout?.receipt_banner_color || "#186A65";
  return (
    <div className="bg-slate-100 rounded-md p-3 border border-slate-200 h-full overflow-auto" data-testid="live-preview">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Live A5 Preview</div>
      {/* A5 = 148 x 210 mm — scale to fit */}
      <div className="mx-auto bg-white shadow-lg p-4" style={{ width: "148mm", minHeight: "210mm", transform: "scale(0.75)", transformOrigin: "top center" }}>
        {show.company_name !== false && contact.company_name && <div style={fontStyle("company_name")}>{contact.company_name?.toUpperCase()}</div>}
        {show.address !== false && contact.address && <div style={fontStyle("address")}>{contact.address}</div>}
        <div style={fontStyle("email")}>
          {[
            show.legal_phone !== false && contact.legal_phone && `Legal: ${contact.legal_phone}`,
            show.property_phone !== false && contact.property_phone && `Property: ${contact.property_phone}`,
            show.whatsapp !== false && contact.whatsapp && `WhatsApp: ${contact.whatsapp}`,
            show.email !== false && contact.email,
          ].filter(Boolean).join("  |  ")}
        </div>
        {show.working_hours !== false && contact.working_hours && <div style={fontStyle("address")}>Working Hours: {contact.working_hours}</div>}
        <div className="my-2 py-1.5 text-center rounded-sm" style={{ ...fontStyle("receipt_heading"), backgroundColor: bannerColor, color: fonts.receipt_heading?.color || "#fff" }}>RECEIPT</div>
        <table className="w-full border border-slate-300 border-collapse mb-2" style={fontStyle("customer")}>
          <tbody>
            <tr><td className="border p-1 text-slate-500">Receipt No</td><td className="border p-1 font-bold">CSC/2026/0001</td><td className="border p-1 text-slate-500">Date</td><td className="border p-1 font-bold">2026-02-29</td></tr>
            <tr><td className="border p-1 text-slate-500">Customer</td><td className="border p-1 font-bold">Sample Customer</td><td className="border p-1 text-slate-500">Mobile</td><td className="border p-1 font-bold">9999999999</td></tr>
            <tr><td className="border p-1 text-slate-500">Service</td><td className="border p-1 font-bold" colSpan={3} style={fontStyle("service")}>Sample Service Name</td></tr>
          </tbody>
        </table>
        {settings?.footer_note && <div style={fontStyle("notes")}>{settings.footer_note}</div>}
        {/* Advertisements */}
        <div className="mt-2 grid gap-2" style={{ gridTemplateColumns: `repeat(${colKeys.length || 1}, 1fr)` }}>
          {colKeys.map((c) => (
            <div key={c} className="flex flex-col gap-1.5">
              {cols[c].map((ad, i) => (
                <div key={i} className="border border-slate-300 rounded-sm overflow-hidden">
                  <div className="text-center py-0.5" style={{ ...fontStyle("ad_heading"), backgroundColor: ad.header_color || "#0A1124", color: fonts.ad_heading?.color || "#fff" }}>{ad.heading}</div>
                  {ad.phone && <div className="text-center bg-slate-100 py-0.5" style={fontStyle("phone")}>☎ Contact: {ad.phone}</div>}
                  <ul className="px-2 py-1">
                    {(ad.items || []).map((it, k) => (
                      <li key={k} style={{ ...fontStyle("ad_list"), fontWeight: it.bold ? 700 : (fonts.ad_list?.bold ? 700 : 400), color: it.bold ? "#0F5132" : fonts.ad_list?.color }}>{it.bold ? "" : "• "}{it.text}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
        {show.website !== false && contact.website && (
          <div className="mt-2" style={fontStyle("footer")}>Website: {contact.website}  |  Thank you for choosing {show.company_name !== false && contact.company_name ? contact.company_name : "us"}</div>
        )}
      </div>
    </div>
  );
}

// ------------------ Contact Details tab ---------------
function ContactTab({ settings, patch }) {
  const c = settings.contact || {};
  const show = c.show || {};
  const set = (k, v) => patch({ contact: { ...c, [k]: v } });
  const setShow = (k, v) => patch({ contact: { ...c, show: { ...show, [k]: v } } });
  return (
    <div className="space-y-3">
      {CONTACT_FIELDS.map((f) => (
        <div key={f.key} className="grid grid-cols-[1fr,auto] gap-3 items-start">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold">{f.label}</Label>
            {f.type === "textarea" ? (
              <Textarea rows={2} value={c[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`contact-${f.key}`} className="mt-1" />
            ) : (
              <Input value={c[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`contact-${f.key}`} className="mt-1 h-9" />
            )}
          </div>
          <label className="mt-6 flex items-center gap-2 text-xs font-semibold">
            <Switch checked={show[f.key] !== false} onCheckedChange={(v) => setShow(f.key, v)} data-testid={`show-${f.key}`} />
            Show
          </label>
        </div>
      ))}
      <div>
        <Label className="text-[11px] uppercase tracking-wider font-bold">Footer Note</Label>
        <Textarea rows={2} value={settings.footer_note || ""} onChange={(e) => patch({ footer_note: e.target.value })} data-testid="footer-note" className="mt-1" />
      </div>
    </div>
  );
}

// ------------------ Advertisement tab ---------------
function AdsTab({ settings, patch }) {
  const ads = settings.advertisements || [];
  const setAds = (next) => patch({ advertisements: next });
  const update = (i, field, value) => setAds(ads.map((a, idx) => idx === i ? { ...a, [field]: value } : a));
  const remove = (i) => setAds(ads.filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= ads.length) return;
    const next = ads.slice(); [next[i], next[j]] = [next[j], next[i]];
    setAds(next.map((a, k) => ({ ...a, order: k })));
  };
  const addAd = () => setAds([...ads, { heading: "New Section", header_color: "#0A1124", phone: "", show: true, order: ads.length, column: 1, items: [] }]);
  const addItem = (i) => update(i, "items", [...(ads[i].items || []), { text: "New Service", bold: false }]);
  const updItem = (i, k, field, v) => update(i, "items", (ads[i].items || []).map((it, idx) => idx === k ? { ...it, [field]: v } : it));
  const rmItem = (i, k) => update(i, "items", (ads[i].items || []).filter((_, idx) => idx !== k));
  return (
    <div className="space-y-4">
      <Button onClick={addAd} className="h-8 text-xs bg-[#186A65] hover:bg-[#0f4d49] text-white" data-testid="add-ad-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Advertisement Section</Button>
      {ads.map((ad, i) => (
        <div key={i} className="border border-slate-200 rounded-md p-3 bg-white space-y-2" data-testid={`ad-${i}`}>
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-slate-400" />
            <Input value={ad.heading} onChange={(e) => update(i, "heading", e.target.value)} placeholder="Heading" className="h-8 flex-1" data-testid={`ad-heading-${i}`} />
            <Input value={ad.header_color || "#0A1124"} onChange={(e) => update(i, "header_color", e.target.value)} type="color" className="h-8 w-14 p-1" />
            <Input value={ad.phone || ""} onChange={(e) => update(i, "phone", e.target.value)} placeholder="Contact phone" className="h-8 w-40" />
            <Select value={String(ad.column || 1)} onValueChange={(v) => update(i, "column", parseInt(v))}>
              <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>Col {n}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-1 text-xs font-semibold"><Switch checked={ad.show !== false} onCheckedChange={(v) => update(i, "show", v)} data-testid={`ad-show-${i}`} />Show</label>
            <button onClick={() => move(i, -1)} className="p-1 hover:bg-slate-100 rounded"><ChevronUp className="h-4 w-4" /></button>
            <button onClick={() => move(i, 1)} className="p-1 hover:bg-slate-100 rounded"><ChevronDown className="h-4 w-4" /></button>
            <button onClick={() => remove(i)} className="p-1 hover:bg-red-50 rounded text-red-600" data-testid={`ad-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="pl-6 space-y-1">
            {(ad.items || []).map((it, k) => (
              <div key={k} className="flex items-center gap-2">
                <Input value={it.text} onChange={(e) => updItem(i, k, "text", e.target.value)} className="h-7 text-xs flex-1" data-testid={`ad-item-${i}-${k}`} />
                <label className="flex items-center gap-1 text-[11px]"><Switch checked={!!it.bold} onCheckedChange={(v) => updItem(i, k, "bold", v)} />Bold</label>
                <button onClick={() => rmItem(i, k)} className="p-1 text-red-500 hover:bg-red-50 rounded"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <Button onClick={() => addItem(i)} className="h-7 text-[11px] bg-slate-200 text-slate-700 hover:bg-slate-300" data-testid={`ad-add-item-${i}`}><Plus className="h-3 w-3 mr-1" /> Add Service</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------ Fonts & Design tab ---------------
function FontsTab({ settings, patch }) {
  const fonts = settings.fonts || {};
  const set = (k, field, v) => patch({ fonts: { ...fonts, [k]: { ...(fonts[k] || {}), [field]: v } } });
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr,110px,80px,70px,110px,90px,60px] gap-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 px-2">
        <div>Section</div><div>Family</div><div>Size (6–24)</div><div>Bold</div><div>Align</div><div>Color</div><div>Leading</div>
      </div>
      {FONT_KEYS.map(({ key, label }) => {
        const c = fonts[key] || {};
        return (
          <div key={key} className="grid grid-cols-[1fr,110px,80px,70px,110px,90px,60px] gap-2 items-center bg-white border border-slate-200 rounded p-2" data-testid={`font-row-${key}`}>
            <div className="text-xs font-semibold">{label}</div>
            <Select value={c.family || "Helvetica"} onValueChange={(v) => set(key, "family", v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min={6} max={24} step={0.5} value={c.size ?? 9} onChange={(e) => set(key, "size", parseFloat(e.target.value))} className="h-8 text-xs font-mono" data-testid={`font-size-${key}`} />
            <div className="flex items-center justify-center"><Switch checked={!!c.bold} onCheckedChange={(v) => set(key, "bold", v)} data-testid={`font-bold-${key}`} /></div>
            <Select value={c.align || "left"} onValueChange={(v) => set(key, "align", v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{ALIGN_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="color" value={c.color || "#000000"} onChange={(e) => set(key, "color", e.target.value)} className="h-8 p-1" data-testid={`font-color-${key}`} />
            <Input type="number" min={4} max={48} step={0.5} value={c.leading ?? 11} onChange={(e) => set(key, "leading", parseFloat(e.target.value))} className="h-8 text-xs font-mono" />
          </div>
        );
      })}
      <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded p-3 mt-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-bold">Banner Color</Label>
          <Input type="color" value={settings.layout?.receipt_banner_color || "#186A65"} onChange={(e) => patch({ layout: { ...(settings.layout || {}), receipt_banner_color: e.target.value } })} className="h-8 p-1 mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-bold">Column Gap (mm)</Label>
          <Input type="number" min={0} max={20} step={0.5} value={settings.layout?.column_gap_mm ?? 2} onChange={(e) => patch({ layout: { ...(settings.layout || {}), column_gap_mm: parseFloat(e.target.value) } })} className="h-8 mt-1 font-mono" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider font-bold">Section Gap (mm)</Label>
          <Input type="number" min={0} max={20} step={0.5} value={settings.layout?.section_gap_mm ?? 3} onChange={(e) => patch({ layout: { ...(settings.layout || {}), section_gap_mm: parseFloat(e.target.value) } })} className="h-8 mt-1 font-mono" />
        </div>
      </div>
    </div>
  );
}

// ------------------ Main Page ---------------
export default function ReceiptSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirtyRef = useRef(false);

  const load = async () => {
    try {
      const { data } = await api.get("/receipt-settings");
      setSettings(data);
      dirtyRef.current = false;
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Failed to load settings"); }
  };
  useEffect(() => { load(); }, []);

  const patch = (delta) => {
    dirtyRef.current = true;
    setSettings((s) => ({ ...s, ...delta }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/receipt-settings", settings);
      setSettings(data);
      dirtyRef.current = false;
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all receipt design settings to factory defaults? This cannot be undone.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/receipt-settings/reset");
      setSettings(data);
      dirtyRef.current = false;
      toast.success("Reset to defaults");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Reset failed"); }
    finally { setBusy(false); }
  };

  const previewPDF = async () => {
    try {
      const { data } = await api.post("/receipt-settings/preview", { settings }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error("Preview failed"); }
  };

  const downloadPDF = async () => {
    try {
      const { data } = await api.post("/receipt-settings/preview", { settings }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "receipt-preview.pdf"; a.click();
    } catch (e) { toast.error("Download failed"); }
  };

  const printPreview = async () => {
    try {
      const { data } = await api.post("/receipt-settings/preview", { settings }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (w) { w.onload = () => w.print(); }
    } catch (e) { toast.error("Print failed"); }
  };

  if (!settings) return <div className="p-8 text-sm text-slate-500">Loading receipt settings…</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr),480px] gap-4" data-testid="receipt-settings-page">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-display font-black text-xl text-slate-900">Receipt Design & Advertisement Settings</div>
            <div className="text-xs text-slate-500">Edits apply to every future receipt PDF · A5 size · admin-only</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={save} disabled={busy} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold" data-testid="save-btn"><Save className="h-4 w-4 mr-1" /> Save Settings</Button>
            <Button onClick={reset} disabled={busy} className="h-9 bg-slate-500 hover:bg-slate-600 text-white text-xs font-semibold" data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-1" /> Reset to Default</Button>
            <Button onClick={previewPDF} className="h-9 bg-[#0B2E5C] hover:bg-[#08234a] text-white text-xs font-semibold" data-testid="preview-btn"><Eye className="h-4 w-4 mr-1" /> Preview PDF</Button>
            <Button onClick={printPreview} className="h-9 bg-[#C89B4A] hover:bg-[#B08536] text-white text-xs font-semibold" data-testid="print-btn"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            <Button onClick={downloadPDF} className="h-9 bg-[#186A65] hover:bg-[#0f4d49] text-white text-xs font-semibold" data-testid="download-btn"><Download className="h-4 w-4 mr-1" /> Download</Button>
            <Button onClick={load} className="h-9 bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-semibold" data-testid="cancel-btn">Cancel</Button>
          </div>
        </div>

        <Tabs defaultValue="contact">
          <TabsList>
            <TabsTrigger value="contact" data-testid="tab-contact">Contact Details</TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-ads">Advertisement Sections</TabsTrigger>
            <TabsTrigger value="fonts" data-testid="tab-fonts">Fonts & Design</TabsTrigger>
          </TabsList>
          <TabsContent value="contact" className="mt-3"><ContactTab settings={settings} patch={patch} /></TabsContent>
          <TabsContent value="ads" className="mt-3"><AdsTab settings={settings} patch={patch} /></TabsContent>
          <TabsContent value="fonts" className="mt-3"><FontsTab settings={settings} patch={patch} /></TabsContent>
        </Tabs>
      </div>

      <div className="xl:sticky xl:top-4 xl:self-start xl:h-[calc(100vh-2rem)]">
        <LivePreview settings={settings} />
      </div>
    </div>
  );
}
